import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Routes } from '../routes'

const LEADER_NAMES = ['Google', 'DHL', 'Kuehne + Nagel', 'Bouygues', 'Jacobs'] as const

interface AuthBrandPanelProps {
    logoSrc: string
    appName: string
}

const AuthBrandPanel = ({ logoSrc, appName }: AuthBrandPanelProps) => {
    const { t } = useTranslation()

    const features = [
        {
            num: '01',
            title: t('views.auth.brandFeature1Title'),
            desc: t('views.auth.brandFeature1Desc'),
        },
        {
            num: '02',
            title: t('views.auth.brandFeature2Title'),
            desc: t('views.auth.brandFeature2Desc'),
        },
        {
            num: '03',
            title: t('views.auth.brandFeature3Title'),
            desc: t('views.auth.brandFeature3Desc'),
        },
    ]

    return (
        <aside className="auth-brand-panel" aria-hidden={false}>
            <div className="auth-brand-glow auth-brand-glow--top" />
            <div className="auth-brand-glow auth-brand-glow--bottom" />

            <svg
                className="auth-brand-curves auth-brand-curves--tr"
                viewBox="0 0 520 420"
                fill="none"
                aria-hidden="true"
            >
                <g stroke="currentColor" strokeWidth="0.7" fill="none">
                    <path d="M520 40 Q 340 80, 220 180 T -20 380" />
                    <path d="M520 70 Q 340 110, 220 210 T -20 410" />
                    <path d="M520 100 Q 340 140, 220 240 T -20 440" />
                    <path d="M520 10 Q 340 50, 220 150 T -20 350" />
                    <path d="M520 -20 Q 340 20, 220 120 T -20 320" />
                </g>
            </svg>

            <svg
                className="auth-brand-curves auth-brand-curves--br"
                viewBox="0 0 560 540"
                fill="none"
                preserveAspectRatio="xMaxYMax meet"
                aria-hidden="true"
            >
                <g stroke="currentColor" strokeWidth="0.7" fill="none">
                    <path d="M560 100 Q 360 140, 240 260 T 0 520" />
                    <path d="M560 140 Q 360 180, 240 300 T 0 560" />
                    <path d="M560 180 Q 360 220, 240 340 T 0 600" />
                    <path d="M560 60 Q 360 100, 240 220 T 0 480" />
                    <path d="M560 20 Q 360 60, 240 180 T 0 440" />
                </g>
            </svg>

            <svg
                className="auth-brand-curves auth-brand-curves--mid-left"
                viewBox="0 0 320 260"
                fill="none"
                aria-hidden="true"
            >
                <g stroke="currentColor" strokeWidth="0.6" fill="none">
                    <path d="M320 30 Q 200 70, 120 130 T -40 240" />
                    <path d="M320 60 Q 200 100, 120 160 T -40 270" />
                    <path d="M320 90 Q 200 130, 120 190 T -40 300" />
                    <path d="M320 0 Q 200 40, 120 100 T -40 210" />
                </g>
            </svg>

            <svg
                className="auth-brand-curves auth-brand-curves--tl"
                viewBox="0 0 240 160"
                fill="none"
                aria-hidden="true"
            >
                <g stroke="currentColor" strokeWidth="0.6" fill="none">
                    <path d="M-10 20 Q 100 40, 180 100 T 260 150" />
                    <path d="M-10 50 Q 100 70, 180 130 T 260 180" />
                </g>
            </svg>

            <div className="auth-brand-content">
                <Link to={Routes.AUTH} className="auth-brand-logo-row">
                    <img src={logoSrc} alt={appName} className="auth-brand-logo" />
                    <span className="auth-brand-app-name">{appName}</span>
                </Link>

                <div className="auth-brand-headline-block">
                    <h2 className="auth-brand-headline">
                        {t('views.auth.brandHeadlineLead')}{' '}
                        <em className="auth-serif auth-brand-headline-accent">
                            {t('views.auth.brandHeadlineAccent')}
                        </em>
                    </h2>
                    <p className="auth-brand-subtitle">{t('views.auth.brandSubtitle')}</p>
                </div>

                <div className="auth-brand-features">
                    {features.map((feature) => (
                        <div key={feature.num} className="auth-brand-feature-card">
                            <div className="auth-mono auth-brand-feature-num">{feature.num}</div>
                            <div>
                                <div className="auth-brand-feature-title">{feature.title}</div>
                                <div className="auth-brand-feature-desc">{feature.desc}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="auth-brand-leaders">
                    <div className="auth-mono auth-brand-leaders-label">
                        {t('views.auth.brandBuiltBy')}
                    </div>
                    <div className="auth-brand-leaders-list">
                        {LEADER_NAMES.map((name) => (
                            <span key={name} className="auth-brand-leader-name">
                                {name}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </aside>
    )
}

export default AuthBrandPanel
